/**
 * PR-G Phase 7.2 — route-level paywall guard для Block 12 (/calculator*) +
 * Block 13 (/contracts*).
 *
 * **Why:** до Phase 7 paywall был enforced ТОЛЬКО на tap из Home.tsx
 * (`handleBlockClick`). Direct URL access — bookmark, browser back/forward,
 * deep-link из share, прямая ввод в URL bar — обходил guard, что нарушало
 * ADR-0014 §«Решение» Q5 paywall semantics.
 *
 * **Behaviour (3 states из usePaywallStatus):**
 *   - `'allowed'` → render children verbatim.
 *   - `'blocked'` → `<Navigate to="/" replace />` (Home.tsx показывает PaywallModal).
 *   - `'pending'` → render `null` (loading flash, ждём `/api/me` fetch).
 *
 * **Self-contained mount-fetch (Phase 7 hotfix):** PaywallGuard сам triggers
 * `apiClient.me.get()` в useEffect при `jwt !== null && subscription === null`.
 * Это нужно для direct URL access: пользователь приходит на /calculator или
 * /contracts/* по bookmark/share/back — App.tsx mounts только на `/`, поэтому
 * его mount-fetch НЕ срабатывает на gated routes. Без self-trigger guard
 * редиректил бы на `/` мгновенно (до того как App.tsx успеет сделать fetch),
 * и user никогда бы не попал на gated screen. Conditions внутри useEffect —
 * mirror App.tsx mount-fetch verbatim:
 *   - jwt === null → guest, skip.
 *   - profile !== null → already loaded, skip.
 *   - subscription !== null → loaded, skip (loop guard).
 *   - cancelled flag — race safety при unmount mid-fetch.
 *   - 401/network/parse error → logout() (graceful guest degradation).
 *
 * **Idempotency relative to App.tsx mount-fetch:** на `/` оба useEffect
 * (App + PaywallGuard если children внутри) могут попытаться выстрелить —
 * но App.tsx wraps только path `/`, а PaywallGuard wraps gated routes — они
 * на разных routes. Совмещение исключено router'ом.
 *
 * **`replace` (а не push)** — direct URL hit не должен создавать history
 * entry на gated route (иначе browser back → снова guard → bounce loop).
 *
 * **Reference:**
 *   - ADR-0014 (5 канонических состояний)
 *   - PR-G Phase 7 hotfix (CI e2e regression — direct URL access race fix)
 *   - `apps/web/src/hooks/usePaywallStatus.ts` (single source of truth)
 *   - `apps/web/src/App.tsx` (mount-fetch pattern source)
 */

import { useEffect, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { usePaywallStatus } from '@/hooks/usePaywallStatus';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/lib/stores/authStore';

export interface PaywallGuardProps {
  children: ReactNode;
}

export function PaywallGuard({ children }: PaywallGuardProps) {
  const status = usePaywallStatus();

  const jwt = useAuthStore((s) => s.jwt);
  const profile = useAuthStore((s) => s.profile);
  const subscription = useAuthStore((s) => s.subscription);
  const setSubscription = useAuthStore((s) => s.setSubscription);
  const logout = useAuthStore((s) => s.logout);

  // Self-contained mount-fetch для direct URL access (Phase 7 hotfix).
  // Mirror App.tsx mount-fetch — те же conditions + same .then/.catch
  // обработка. Срабатывает только когда user landed direct на gated route
  // (App.tsx на `/` не mount'ился, fetch не происходил).
  useEffect(() => {
    if (jwt === null || profile !== null || subscription !== null) return;
    let cancelled = false;
    apiClient.me
      .get()
      .then((me) => {
        if (cancelled) return;
        setSubscription({
          status: me.subscriptionStatus,
          trialStartedAt: me.trialStartedAt,
          expiresAt: me.expiresAt,
        });
      })
      .catch(() => {
        if (cancelled) return;
        logout();
      });
    return () => {
      cancelled = true;
    };
  }, [jwt, profile, subscription, setSubscription, logout]);

  if (status === 'pending') {
    // Brief loading flash — ждём fetch resolution. Не redirect (иначе bounce
    // на `/` до того, как subscription загрузится). Не показываем содержимое
    // gated route раньше, чем подписка проверена.
    return null;
  }
  if (status === 'blocked') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
